import {
  Target, Megaphone, LifeBuoy, BarChart3, Layers, Check,
} from "lucide-react";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import { Panel, PageHead } from "@/components/Panel";
import CTABand from "@/components/CTABand";

const services = [
  {
    icon: Target,
    label: "Sales Operations",
    text: "Your pipeline should be a forecasting instrument, not a wish list. We rebuild CRM architecture around how your team actually sells, install forecasting discipline leadership can trust, and make pipeline management something reps do in minutes rather than hours.",
    bullets: [
      "CRM strategy, architecture, and hygiene",
      "Forecasting models and cadence",
      "Pipeline management and stage definitions",
      "Territory, quota, and comp plan support",
      "Sales process design and enablement workflows",
    ],
  },
  {
    icon: Megaphone,
    label: "Marketing Operations",
    text: "Marketing should be able to prove its contribution to revenue. We optimize the funnel end to end, build reporting that ties spend to closed-won, and automate the work that shouldn't need a human.",
    bullets: [
      "Funnel optimization and lifecycle stage design",
      "Campaign reporting and attribution",
      "Marketing automation build-out and cleanup",
      "Lead scoring, routing, and MQL/SQL definitions",
      "Data hygiene and list management",
    ],
  },
  {
    icon: LifeBuoy,
    label: "Customer Success Operations",
    text: "Retention is the cheapest growth you'll ever buy. We put structure around CS so churn becomes something you see coming and can prevent.",
    bullets: [
      "Churn reduction strategy and risk signals",
      "Retention and renewal process design",
      "Health scoring and account segmentation",
      "Onboarding and QBR workflows",
      "Expansion and upsell motion design",
    ],
  },
  {
    icon: BarChart3,
    label: "Data & Analytics",
    text: "Numbers only matter if someone acts on them. We build the dashboards your leadership team will actually open on Monday morning.",
    bullets: [
      "Executive and functional dashboards",
      "KPI definition and tracking frameworks",
      "Revenue reporting and cohort analysis",
      "Actionable recommendations, not just charts",
      "Single source of truth across systems",
    ],
  },
  {
    icon: Layers,
    label: "Tech Stack Optimization",
    text: "Most companies pay for more tooling than they use. We evaluate what you have, integrate what matters, and cut what doesn't earn its keep.",
    bullets: [
      "Tool integration and systems architecture",
      "Platform evaluation and selection",
      "Tech ROI analysis and consolidation",
      "Data flow mapping between systems",
      "Admin and governance model",
    ],
  },
];

export default function Services() {
  return (
    <div data-testid="services-page">
      <SEO
        title="RevOps Services — Sales, Marketing & CS Operations | RevGen Genius"
        description="Sales operations, marketing operations, customer success operations, data and analytics, and tech stack optimization — delivered as one integrated RevOps practice."
        path="/services"
      />
      <div className="mx-auto max-w-[1240px] space-y-24 px-5 sm:px-8">
        <PageHead
          testId="services-head"
          label="Services"
          title="Revenue operations services"
          intro="Every engagement is scoped to what your revenue engine actually needs. Most clients start with one discipline and expand as the system comes together."
        />

        <div className="space-y-10">
          {services.map((s, i) => (
            <Reveal key={s.label}>
              <Panel label={s.label} tint={i % 2 === 1} className="pt-16">
                <div className="grid gap-10 lg:grid-cols-2">
                  <div className="flex items-start gap-4">
                    <s.icon size={28} strokeWidth={1.8} className="mt-1 shrink-0 text-[#1F5FFF]" aria-hidden="true" />
                    <p data-testid={`service-text-${i}`} className="text-[1.05rem] leading-relaxed text-[#17203A]">
                      {s.text}
                    </p>
                  </div>
                  <ul className="space-y-3.5 lg:border-l lg:border-[#1F5FFF]/20 lg:pl-10">
                    {s.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-3 text-[0.98rem] font-medium text-[#17203A]">
                        <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#1F5FFF]">
                          <Check size={12} strokeWidth={3.5} className="text-white" aria-hidden="true" />
                        </span>
                        {b}
                      </li>
                    ))}
                  </ul>
                </div>
              </Panel>
            </Reveal>
          ))}
        </div>

        <CTABand
          title="Not sure where to start?"
          text="Most engagements begin with a free assessment. We'll tell you which discipline will move your numbers first."
          testId="services-cta-book-button"
        />
      </div>
    </div>
  );
}
